import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import ImageUpload from "../../Components/ImageUpload";

const EMPTY_FORM = {
  name: "",
  description: "",
  image: "",
  deliveryCharge: "50",
  active: true,
};

// Every Item belongs to one Restaurant, and the storefront homepage lists
// active restaurants as a directory - this is the management UI for
// adding a new one, or hiding one (e.g. temporarily closed) without
// deleting its menu.
const Restaurants = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("authToken");

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/restaurant?all=true`);
      setRestaurants(response.data.restaurants);
    } catch (err) {
      console.error("Error fetching restaurants:", err);
      setError("Couldn't load restaurants.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const startCreate = () => {
    setError(null);
    setEditingId(null);
    setFormData(EMPTY_FORM);
  };

  const startEdit = (restaurant) => {
    setError(null);
    setEditingId(restaurant._id);
    setFormData({
      name: restaurant.name,
      description: restaurant.description || "",
      image: restaurant.image || "",
      deliveryCharge: String(restaurant.deliveryCharge),
      active: restaurant.active,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError("Name is required.");
      return;
    }
    const deliveryCharge = Number(formData.deliveryCharge);
    if (Number.isNaN(deliveryCharge) || deliveryCharge < 0) {
      setError("Delivery charge must be a non-negative number.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = { ...formData, deliveryCharge };
      if (editingId) {
        await axios.put(`${API_BASE_URL}/restaurant/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_BASE_URL}/restaurant`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      startCreate();
      await fetchRestaurants();
    } catch (err) {
      console.error("Error saving restaurant:", err);
      setError(err.response?.data?.message || "Failed to save.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (restaurant) => {
    try {
      await axios.put(
        `${API_BASE_URL}/restaurant/${restaurant._id}`,
        { active: !restaurant.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchRestaurants();
    } catch (err) {
      console.error("Error updating restaurant:", err);
      alert(err.response?.data?.message || "Failed to update restaurant.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this restaurant? This can't be undone.")) return;
    try {
      await axios.delete(`${API_BASE_URL}/restaurant/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (editingId === id) startCreate();
      await fetchRestaurants();
    } catch (err) {
      console.error("Error deleting restaurant:", err);
      alert(err.response?.data?.message || "Failed to delete.");
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sub">Loading restaurants…</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4 text-ink">Restaurants</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {restaurants.length === 0 ? (
            <p className="text-sub">No restaurants yet - add the first one.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {restaurants.map((restaurant) => (
                <div key={restaurant._id} className="bg-card border border-line rounded-lg p-4">
                  {restaurant.image && (
                    <img
                      src={restaurant.image}
                      alt={restaurant.name}
                      className="w-full h-32 object-cover rounded mb-2"
                    />
                  )}
                  <h3 className="text-lg font-semibold text-ink">{restaurant.name}</h3>
                  {restaurant.description && (
                    <p className="text-sm text-sub">{restaurant.description}</p>
                  )}
                  <p className="text-sm text-sub mt-1">
                    Delivery: <span className="font-mono text-ink">৳{restaurant.deliveryCharge}</span>
                  </p>
                  <span
                    className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                      restaurant.active
                        ? "bg-green-50 text-green-700"
                        : "bg-red-50 text-red-700"
                    }`}
                  >
                    {restaurant.active ? "Active" : "Hidden"}
                  </span>
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={() => startEdit(restaurant)}
                      className="bg-primary text-white py-2 px-4 rounded hover:opacity-90 transition"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => toggleActive(restaurant)}
                      className="text-xs font-semibold text-ink border border-line px-3 rounded hover:bg-paper transition"
                    >
                      {restaurant.active ? "Hide" : "Show"}
                    </button>
                    <button
                      onClick={() => handleDelete(restaurant._id)}
                      className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-card border border-line rounded-lg p-6 space-y-4 h-fit"
        >
          <h3 className="text-lg font-semibold text-ink">
            {editingId ? "Edit restaurant" : "Add a restaurant"}
          </h3>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </p>
          )}

          <input
            placeholder="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full p-2 border border-line rounded"
          />
          <input
            placeholder="Short description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full p-2 border border-line rounded"
          />
          <ImageUpload
            label="Image"
            value={formData.image}
            onChange={(image) => setFormData({ ...formData, image })}
          />
          <div>
            <label className="block font-semibold mb-1 text-ink">Delivery Charge (৳)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.deliveryCharge}
              onChange={(e) => setFormData({ ...formData, deliveryCharge: e.target.value })}
              className="w-full p-2 border border-line rounded"
            />
          </div>
          <label className="flex items-center text-ink">
            <input
              type="checkbox"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="mr-2"
            />
            Active (visible in the storefront directory)
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-white p-3 rounded-lg font-bold hover:opacity-90 transition disabled:opacity-60"
          >
            {submitting ? "Saving…" : editingId ? "Save Changes" : "Add Restaurant"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={startCreate}
              className="w-full border border-line text-ink py-2 rounded hover:bg-paper transition"
            >
              Cancel Edit
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default Restaurants;
